import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Collapse,
  IconButton,
  TextField
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { PointOfContact } from './forms/types/PointOfContact'
import {
  trimWhitespace,
  collapseWhitespace,
  replaceUnicodeWhitespaceWithSpace,
  convertLiteralUnicodeToActual
} from 'utils/whitespaceUtils'

type Props = {
  data: PointOfContact | any
  onSubmit: (updated: any) => void
  onClear?: () => void
  disabled?: boolean
  required?: boolean
  refObj?: {
    firstName?: React.RefObject<HTMLInputElement>
  }
}

const PointOfContactEditor = ({
  data,
  onSubmit,
  required,
  onClear,
  disabled = false,
  refObj
}: Props) => {
  const [expanded, setExpanded] = useState(true)

  const formik = useFormik<PointOfContact>({
    initialValues: {
      person: {
        firstName: data?.person?.firstName || '',
        lastName: data?.person?.lastName || '',
        email: data?.person?.email || '',
        phone: data?.person?.phone || ''
      }
    },
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: false,
    validationSchema: Yup.object({
      person: Yup.object().shape({
        firstName: Yup.string()
          .required('Required')
          .matches(/^[a-zA-Z\s\-'.]+$/, 'First name must not contain numbers'),
        lastName: Yup.string()
          .required('Required')
          .matches(/^[a-zA-Z\s\-'.]+$/, 'Last name must not contain numbers'),
        email: Yup.string().email('Invalid email').required('Required'),
        phone: Yup.string()
          .required('Required')
          .test('phone-format', 'Phone must contain only digits, spaces, hyphens, parentheses, or plus sign', (value) => {
            if (!value) return true // let .required() handle empty case
            return /^[+]?[0-9\s\-()]+$/.test(value)
          })
          .test('phone-length', 'Phone must be at least 7 digits', (value) => {
            if (!value) return true // let .required() handle empty case
            const digitsOnly = value.replace(/\D/g, '')
            return digitsOnly.length >= 7
          })
      })
    }),
    onSubmit: (values) => {
      onSubmit(values)
    }
  })

  // Helper function to sanitize name input - handle Unicode whitespace
  const sanitizeNameInput = (value: string): string => {
    // First, convert any literal Unicode escape sequences to actual Unicode characters
    let sanitized = convertLiteralUnicodeToActual(value)

    // Replace all Unicode whitespaces with ASCII space
    sanitized = replaceUnicodeWhitespaceWithSpace(sanitized)

    // Collapse consecutive spaces to single space
    sanitized = collapseWhitespace(sanitized)

    // Don't trim during onChange - allow user to type freely
    return sanitized
  }

  // Auto-expand if required and empty on mount
  useEffect(() => {
    if (required && !data?.person) {
      setExpanded(true)
    }
  }, [required])

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          border: required ? '1px solid red' : '1px solid #d0d7de',
          borderRadius: 2,
          p: 3,
          mt: 3,
          mb: 3,
          backgroundColor: '#f9fafb',
          width: '75%',
          maxWidth: '100%'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center">
            <Typography variant="h6">
              <span>Beneficiary Point of Contact</span>
            </Typography>
          </Box>
          <IconButton onClick={() => setExpanded(!expanded)} size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={6} md={6}>
              <TextField
                fullWidth
                label="First Name"
                inputRef={refObj?.firstName}
                name="person.firstName"
                autoComplete="off"
                inputProps={{
                  spellCheck: 'false',
                  autoCorrect: 'off'
                }}
                value={formik.values.person.firstName}
                onChange={(e) => {
                  const sanitized = sanitizeNameInput(e.target.value)

                  formik.setFieldValue('person.firstName', sanitized)
                }}
                onPaste={(e) => {
                  e.preventDefault()
                  const pastedText = e.clipboardData?.getData('text') || ''
                  const sanitized = sanitizeNameInput(pastedText)
                  formik.setFieldValue('person.firstName', sanitized)
                }}
                onBlur={() => {
                  // On blur, trim leading/trailing spaces
                  const trimmed = trimWhitespace(formik.values.person.firstName)
                  formik.setFieldValue('person.firstName', trimmed)
                  formik.setFieldTouched('person.firstName', true)
                  // Validate after the value has been updated
                  setTimeout(() => {
                    formik.validateField('person.firstName')
                  }, 0)
                }}
                error={formik.touched.person?.firstName && Boolean(formik.errors.person?.firstName)}
                helperText={formik.touched.person?.firstName && formik.errors.person?.firstName}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="person.lastName"
                autoComplete="off"
                inputProps={{
                  spellCheck: 'false',
                  autoCorrect: 'off'
                }}
                value={formik.values.person.lastName}
                onChange={(e) => {
                  const sanitized = sanitizeNameInput(e.target.value)
                  formik.setFieldValue('person.lastName', sanitized)
                }}
                onPaste={(e) => {
                  e.preventDefault()
                  const pastedText = e.clipboardData?.getData('text') || ''
                  const sanitized = sanitizeNameInput(pastedText)
                  formik.setFieldValue('person.lastName', sanitized)
                }}
                onBlur={() => {
                  // On blur, trim leading/trailing spaces
                  const trimmed = trimWhitespace(formik.values.person.lastName)
                  formik.setFieldValue('person.lastName', trimmed)
                  formik.setFieldTouched('person.lastName', true)
                  // Validate after the value has been updated
                  setTimeout(() => {
                    formik.validateField('person.lastName')
                  }, 0)
                }}
                error={formik.touched.person?.lastName && Boolean(formik.errors.person?.lastName)}
                helperText={formik.touched.person?.lastName && formik.errors.person?.lastName}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="person.email"
                autoComplete="off"
                value={formik.values.person.email}
                onChange={(e) => {
                  // Replace Unicode whitespaces and collapse consecutive spaces for email
                  const replaced = replaceUnicodeWhitespaceWithSpace(e.target.value)
                  const collapsed = collapseWhitespace(replaced)
                  formik.setFieldValue('person.email', collapsed)
                }}
                onPaste={(e) => {
                  e.preventDefault()
                  const pastedText = e.clipboardData?.getData('text') || ''
                  const replaced = replaceUnicodeWhitespaceWithSpace(pastedText)
                  const collapsed = collapseWhitespace(replaced)
                  formik.setFieldValue('person.email', collapsed)
                }}
                onBlur={() => {
                  const trimmed = trimWhitespace(formik.values.person.email)
                  formik.setFieldValue('person.email', trimmed)
                  formik.setFieldTouched('person.email', true)
                  // Validate after the value has been updated
                  setTimeout(() => {
                    formik.validateField('person.email')
                  }, 0)
                }}
                error={formik.touched.person?.email && Boolean(formik.errors.person?.email)}
                helperText={formik.touched.person?.email && formik.errors.person?.email}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <TextField
                fullWidth
                label="Phone"
                name="person.phone"
                autoComplete="off"
                value={formik.values.person.phone}
                onChange={(e) => {
                  // Allow user to type anything without real-time stripping
                  formik.setFieldValue('person.phone', e.target.value)
                }}
                onPaste={(e) => {
                  e.preventDefault()
                  const pastedText = e.clipboardData?.getData('text') || ''
                  formik.setFieldValue('person.phone', pastedText)
                }}
                onBlur={() => {
                  const originalValue = formik.values.person.phone
                  const hasInvalidChars = /[^+0-9\s\-()]/.test(originalValue)
                  
                  // Strip non-allowed characters
                  let value = originalValue.replace(/[^+0-9\s\-()]/g, '')
                  
                  // Replace Unicode whitespaces and collapse consecutive spaces
                  const replaced = replaceUnicodeWhitespaceWithSpace(value)
                  const collapsed = collapseWhitespace(replaced)
                  const trimmed = trimWhitespace(collapsed)
                  
                  formik.setFieldValue('person.phone', trimmed)
                  formik.setFieldTouched('person.phone', true)
                  
                  // If invalid characters were present, show format error
                  if (hasInvalidChars) {
                    formik.setFieldError('person.phone', 'Phone must contain only digits, spaces, hyphens, parentheses, or plus sign')
                  } else {
                    // Otherwise validate normally after value is updated
                    setTimeout(() => {
                      formik.validateField('person.phone')
                    }, 0)
                  }
                }}
                error={formik.touched.person?.phone && Boolean(formik.errors.person?.phone)}
                helperText={
                  formik.touched.person?.phone 
                    ? (formik.errors.person?.phone as string) || ''
                    : ''
                }
              />
            </Grid>
          </Grid>

          {!disabled && (
            <Box mt={3} display="flex" gap={2}>
              <Button
                variant="contained"
                type="button"
                onClick={() => {
                  formik.validateForm().then((errors) => {
                    if (Object.keys(errors).length === 0) {
                      onSubmit(formik.values)
                    } else {
                      formik.setTouched({
                        person: {
                          firstName: true,
                          lastName: true,
                          email: true,
                          phone: true
                        }
                      })
                    }
                  })
                }}
              >
                Confirm
              </Button>
            </Box>
          )}
        </Collapse>
        {required && (
          <Box mt={2}>
            <Typography variant="body2" color="error">
              Please complete required contact details
            </Typography>
          </Box>
        )}
      </Paper>
    </>
  )
}

export default PointOfContactEditor
